import { useEffect, useRef, useState } from "react";
import { Scissors, Route } from "lucide-react";
import { MAP_VIEW, project, useIslandPaths, LANDMARKS, xToLng, yToLat } from "../lib/nzMap";

// Preset transects across famous NZ tectonic features. Bearings are in
// degrees clockwise from north (matching the slice.theta convention).
const TRANSECT_PRESETS = [
  {
    id: "alpine",
    name: "Alpine Fault",
    hint: "NE-SW · along",
    lng: 170.5,
    lat: -43.6,
    bearing: 55,
    halfWidth: 0.4,
    narration:
      "A 600-kilometre plate boundary where the Pacific and Australian plates grind past each other at ~30 mm/year — the next big rupture is only a matter of when.",
    story: [
      "The Alpine Fault is the longest strike-slip fault above water on Earth, and the geological engine that keeps building Kā Tiritiri o te Moana — the Southern Alps. It has slipped roughly 480 km horizontally over 25 million years and vertically ~20 km, still lifting the range faster than erosion can wear it down.",
      "Palaeoseismic trenches on the fault show a remarkably clock-like recurrence of magnitude 8 ruptures every ~250-300 years. The last major event was in 1717; scientists estimate a ~75% probability of another M8 within the next 50 years — one of the highest known anywhere on the planet.",
    ],
    learnMoreUrl: "https://en.wikipedia.org/wiki/Alpine_Fault",
    learnMoreLabel: "Wikipedia · Alpine Fault",
  },
  {
    id: "hikurangi",
    name: "Hikurangi Slab",
    hint: "NW-SE · across trench",
    lng: 177.0,
    lat: -39.5,
    bearing: 135,
    halfWidth: 0.8,
    narration:
      "The Pacific plate diving beneath Aotearoa at ~5 cm/year — watch the tremors step deeper as your eye tracks westward down the slab.",
    story: [
      "Off the east coast of the North Island, the Pacific plate bends down and slides beneath the Australian plate at about 5 cm/year, forming the Hikurangi Subduction Zone. The deepest tremors you see on the map (cool blues, 300+ km down) are the slab still shaking as it sinks into the mantle.",
      "Some patches of the megathrust are 'locked' — they don't slip until they rupture in a great earthquake. Others release strain quietly in slow-slip events lasting weeks, silent quakes that GeoNet tracks with GPS and pressure gauges on the seafloor. A full Hikurangi rupture (~M9) is the largest earthquake and tsunami threat modelled for Aotearoa.",
    ],
    learnMoreUrl: "https://en.wikipedia.org/wiki/Hikurangi_Margin",
    learnMoreLabel: "Wikipedia · Hikurangi Margin",
  },
  {
    id: "taupo",
    name: "Taupō Rift",
    hint: "NW-SE · across zone",
    lng: 176.1,
    lat: -38.7,
    bearing: 130,
    halfWidth: 0.5,
    narration:
      "A young volcanic rift pulling itself apart at ~7 mm/year; the shallowest shocks trace magma pockets and geothermal plumbing.",
    story: [
      "The Taupō Volcanic Zone is a young continental rift (about 2 million years old) thinning the crust from White Island to Ruapehu. It hosts one-fifth of all known caldera-forming eruptions on Earth, including the Ōruanui event 25,500 years ago that ejected ~1,170 km³ of magma.",
      "The 232 CE Hatepe eruption at Taupō launched pyroclastic flows that reshaped much of the central North Island in minutes. Most of the shallow rust dots on this slice are microearthquakes driven by moving fluids and magma beneath the rift's geothermal fields.",
    ],
    learnMoreUrl: "https://en.wikipedia.org/wiki/Taup%C5%8D_Volcanic_Zone",
    learnMoreLabel: "Wikipedia · Taupō Volcanic Zone",
  },
  {
    id: "cook",
    name: "Cook Strait",
    hint: "N-S · gap between",
    lng: 174.6,
    lat: -41.3,
    bearing: 0,
    halfWidth: 0.6,
    narration:
      "The narrow gap where the fault system bends between the islands; restless shallow shocks live in the bend, right beneath the ferries.",
    story: [
      "Cook Strait is a tectonic hinge — the plate boundary transitions here from subduction in the north to the strike-slip Alpine Fault in the south. The Wairarapa, Wellington, and Awatere faults all converge in and around the strait, making it one of the most seismically busy pockets in the country.",
      "In 1855 the Wairarapa rupture (M8.1-8.2) lifted parts of Wellington by up to six metres and remains the largest historical earthquake in Aotearoa. Almost every ferry passenger crosses directly over the trace of that fault without realising it.",
    ],
    learnMoreUrl: "https://en.wikipedia.org/wiki/1855_Wairarapa_earthquake",
    learnMoreLabel: "Wikipedia · 1855 Wairarapa quake",
  },
];

const IDS = {
  map: "epicenter-map",
  dot: "quake-dot",
  radar: "radar-sweep",
};

// Radar rotates around this pivot. Roughly the geographic middle of the
// two islands so the sweep line reaches everywhere.
const RADAR = {
  cx: MAP_VIEW.w * 0.55,
  cy: MAP_VIEW.h * 0.52,
  period: 14, // seconds per full rotation
  reach: Math.hypot(MAP_VIEW.w, MAP_VIEW.h) * 0.75,
};

// Angle from the radar pivot to (x, y), in the same convention as SVG rotate
// (0deg points up, increases clockwise). The sweep line starts pointing up
// (from cx,cy going to cx, cy - reach) then rotates clockwise.
const dotDelay = (x, y) => {
  const dx = x - RADAR.cx;
  const dy = y - RADAR.cy;
  let deg = (Math.atan2(dx, -dy) * 180) / Math.PI; // 0=up, 90=right
  if (deg < 0) deg += 360;
  return (deg / 360) * RADAR.period;
};

const magRadius = (m) => {
  if (m == null) return 3;
  return Math.max(2.5, Math.min(22, Math.pow(m, 1.7) * 0.8));
};

// Depth-based colouring. Shallow quakes (which the dog actually feels) sit at
// the warm end of the scale; deep subduction quakes fade to cool blue-grey.
const DEPTH_BINS = [
  { max: 15, color: "hsl(14 66% 32%)", label: "≤15 km", feels: true, note: "he'd feel it" },
  { max: 40, color: "hsl(22 60% 40%)", label: "15–40 km" },
  { max: 100, color: "hsl(30 28% 32%)", label: "40–100 km" },
  { max: 300, color: "hsl(210 14% 42%)", label: "100–300 km" },
  { max: Infinity, color: "hsl(218 22% 55%)", label: ">300 km" },
];

const depthBin = (d) => {
  if (d == null) return DEPTH_BINS[2];
  for (const b of DEPTH_BINS) if (d <= b.max) return b;
  return DEPTH_BINS[DEPTH_BINS.length - 1];
};

const depthColor = (d) => depthBin(d).color;

// Legacy magnitude colour kept for the "wake threshold" fill flash but
// most of the map now uses depthColor for the dot itself.
const magColor = (m) => {
  if (m == null) return "hsl(24 22% 10%)";
  if (m >= 6) return "hsl(14 66% 32%)";
  if (m >= 4.5) return "hsl(14 60% 42%)";
  return "hsl(24 22% 20%)";
};

const fmtAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s`;
  if (diff < 3600) return `${Math.round(diff / 60)}m`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h`;
  return `${Math.round(diff / 86400)}d`;
};

export default function EpicenterMap({ quakes = [], slice, setSlice }) {
  const { north, south, northGhost, southGhost } = useIslandPaths();
  const [hovered, setHovered] = useState(null);
  const [nowKey, setNowKey] = useState(0);
  const svgRef = useRef(null);
  const draggingRef = useRef(false);

  // Force a re-render every 30s so "ago" labels stay fresh
  useEffect(() => {
    const t = setInterval(() => setNowKey((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const visible = quakes
    .filter(
      (q) =>
        typeof q.longitude === "number" &&
        typeof q.latitude === "number" &&
        q.longitude >= 165 &&
        q.longitude <= 180 &&
        q.latitude >= -48 &&
        q.latitude <= -33
    )
    .slice(0, 60);

  // Newest first is already in `quakes`; sort by magnitude ascending so
  // small ones render first (bigger drawn on top for readability).
  const ordered = [...visible].sort(
    (a, b) => (a.magnitude ?? 0) - (b.magnitude ?? 0)
  );

  const compass = (
    <g>
      <circle cx="42" cy="46" r="18" fill="none" stroke="hsl(24 22% 10%)" strokeWidth="0.6" opacity="0.75" />
      <path d="M42 30 L38 50 L42 46 L46 50 Z" fill="hsl(24 22% 10%)" opacity="0.9" />
      <text x="42" y="24" textAnchor="middle" className="mono" fontSize="7" fill="hsl(24 22% 22%)" letterSpacing="2">
        N
      </text>
    </g>
  );

  // Convert a client-space point into the SVG viewBox coords.
  const clientToSvg = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * MAP_VIEW.w,
      y: ((clientY - rect.top) / rect.height) * MAP_VIEW.h,
    };
  };

  // Generic drag starter -- `mode` is "translate", "rotate", or "width".
  const startDrag = (mode) => (e) => {
    if (!slice?.active) return;
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = mode;
    const apply = (ev) => {
      const cx = ev.clientX ?? ev.touches?.[0]?.clientX;
      const cy = ev.clientY ?? ev.touches?.[0]?.clientY;
      if (typeof cx !== "number" || typeof cy !== "number") return;
      const { x, y } = clientToSvg(cx, cy);
      if (mode === "translate") {
        const lng = xToLng(x);
        const lat = yToLat(y);
        setSlice((s) => ({ ...s, lng, lat, presetId: null }));
      } else if (mode === "rotate") {
        setSlice((s) => {
          const [ccx, ccy] = project(s.lng, s.lat);
          const dx = x - ccx;
          const dy = y - ccy;
          const theta = Math.atan2(dx, -dy);
          return { ...s, theta, presetId: null };
        });
      } else if (mode === "width") {
        setSlice((s) => {
          const [ccx, ccy] = project(s.lng, s.lat);
          // Project pointer onto the perpendicular direction of the current
          // transect. Perp SVG vector = (cos(theta), sin(theta)).
          const perpX = Math.cos(s.theta);
          const perpY = Math.sin(s.theta);
          const dx = x - ccx;
          const dy = y - ccy;
          const perpDistPx = Math.abs(dx * perpX + dy * perpY);
          const degToPx =
            (MAP_VIEW.w / (179.5 - 165.5) + MAP_VIEW.h / (47.5 - 33.5)) / 2;
          const widthDeg = Math.max(0.2, Math.min(2, perpDistPx / degToPx));
          return { ...s, halfWidth: widthDeg, presetId: null };
        });
      }
    };
    apply(e);
    const onMove = (ev) => apply(ev);
    const onUp = () => {
      draggingRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const toggleSlice = () =>
    setSlice && setSlice((s) => ({ ...s, active: !s.active }));

  const applyPreset = (p) => {
    if (!setSlice) return;
    setSlice({
      active: true,
      lng: p.lng,
      lat: p.lat,
      theta: (p.bearing * Math.PI) / 180,
      halfWidth: p.halfWidth,
      presetId: p.id,
      presetName: p.name,
      presetNarration: p.narration,
      presetStory: p.story,
      presetLearnMoreUrl: p.learnMoreUrl,
      presetLearnMoreLabel: p.learnMoreLabel,
    });
  };

  const activePresetId = slice?.presetId ?? null;

  return (
    <section
      data-testid={IDS.map}
      className="relative border border-[hsl(var(--ink))]/40 bg-[hsl(var(--paper-2))]/50 overflow-hidden"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-[hsl(var(--ink))]/25 gap-3 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--ink-2))]">
            Epicentres · what he feels
          </div>
          <div className="font-display italic text-lg mt-0.5">Aotearoa, this week</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {slice?.active && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="preset-transects">
              <Route size={12} className="text-[hsl(var(--ink-mid))]" />
              {TRANSECT_PRESETS.map((p) => (
                <button
                  key={p.id}
                  data-testid={`preset-transect-${p.id}`}
                  onClick={() => applyPreset(p)}
                  className={`mono text-[9px] uppercase tracking-[0.18em] px-2 py-1 border transition-colors ${
                    activePresetId === p.id
                      ? "bg-[hsl(var(--seismic))] text-[hsl(var(--paper))] border-[hsl(var(--seismic))]"
                      : "border-[hsl(var(--ink))]/40 hover:bg-[hsl(var(--ink))]/5 hover:border-[hsl(var(--seismic))]/60"
                  }`}
                  title={`${p.name} · ${p.hint}`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
          <button
            data-testid="slice-toggle"
            onClick={toggleSlice}
            className={`mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${
              slice?.active
                ? "bg-[hsl(var(--seismic))] text-[hsl(var(--paper))] border-[hsl(var(--seismic))]"
                : "border-[hsl(var(--ink))]/40 hover:bg-[hsl(var(--ink))]/5"
            }`}
            aria-pressed={!!slice?.active}
            title="Drag the centre to move · drag the top handle to rotate"
          >
            <Scissors size={12} />
            {slice?.active
              ? `Slice · ${(((slice.theta * 180) / Math.PI + 360) % 360).toFixed(0)}°`
              : "Slice"}
          </button>
          <div className="mono text-[10px] text-[hsl(var(--ink-mid))] hidden sm:block" key={nowKey}>
            {visible.length} plotted
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_260px]">
        <div className="relative">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${MAP_VIEW.w} ${MAP_VIEW.h}`}
            className="w-full h-auto"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="hatchSea"
                width="6"
                height="6"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(24)"
              >
                <line
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="6"
                  stroke="hsl(30 22% 55%)"
                  strokeWidth="0.35"
                  opacity="0.35"
                />
              </pattern>
              <radialGradient id="ripple" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="hsl(14 66% 32%)" stopOpacity="0.4" />
                <stop offset="100%" stopColor="hsl(14 66% 32%)" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="radarTail" x1="0" y1="0" x2={-RADAR.reach} y2={RADAR.reach * 0.5} gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="hsl(14 66% 32%)" stopOpacity="0.24" />
                <stop offset="100%" stopColor="hsl(14 66% 32%)" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Sea hatching */}
            <rect width="100%" height="100%" fill="url(#hatchSea)" />

            {/* Radar rings + crosshair centred at (cx, cy) */}
            <g opacity="0.35">
              {[0.28, 0.5, 0.72, 0.95].map((frac) => (
                <circle
                  key={frac}
                  cx={RADAR.cx}
                  cy={RADAR.cy}
                  r={RADAR.reach * frac}
                  fill="none"
                  stroke="hsl(24 22% 10%)"
                  strokeWidth="0.4"
                  strokeDasharray="1 4"
                />
              ))}
              <line
                x1={RADAR.cx - RADAR.reach}
                y1={RADAR.cy}
                x2={RADAR.cx + RADAR.reach}
                y2={RADAR.cy}
                stroke="hsl(24 22% 10%)"
                strokeWidth="0.3"
                strokeDasharray="2 4"
              />
              <line
                x1={RADAR.cx}
                y1={RADAR.cy - RADAR.reach}
                x2={RADAR.cx}
                y2={RADAR.cy + RADAR.reach}
                stroke="hsl(24 22% 10%)"
                strokeWidth="0.3"
                strokeDasharray="2 4"
              />
            </g>

            {compass}

            {/* Scale bar */}
            <g transform={`translate(20 ${MAP_VIEW.h - 32})`}>
              <line x1="0" y1="0" x2="80" y2="0" stroke="hsl(24 22% 10%)" strokeWidth="1.2" />
              <line x1="0" y1="-3" x2="0" y2="3" stroke="hsl(24 22% 10%)" strokeWidth="1.2" />
              <line x1="80" y1="-3" x2="80" y2="3" stroke="hsl(24 22% 10%)" strokeWidth="1.2" />
              <text x="40" y="14" textAnchor="middle" className="mono" fontSize="8" fill="hsl(24 22% 25%)">
                ~200 km
              </text>
            </g>

            {/* Ghost outlines (hand-drawn double-ink) */}
            <path d={northGhost} fill="none" stroke="hsl(24 22% 18%)" strokeWidth="0.6" opacity="0.35" />
            <path d={southGhost} fill="none" stroke="hsl(24 22% 18%)" strokeWidth="0.6" opacity="0.35" />

            {/* Island fills */}
            <path d={north} fill="hsl(41 44% 88%)" stroke="hsl(24 22% 10%)" strokeWidth="1.2" />
            <path d={south} fill="hsl(41 44% 88%)" stroke="hsl(24 22% 10%)" strokeWidth="1.2" />

            {/* Landmark dots */}
            {LANDMARKS.map((l) => {
              const [x, y] = project(l.lng, l.lat);
              return (
                <g key={l.name}>
                  <circle cx={x} cy={y} r="1.5" fill="hsl(24 22% 15%)" />
                  <text
                    x={x + 6}
                    y={y + 3}
                    className="mono"
                    fontSize="8"
                    fill="hsl(24 22% 25%)"
                    style={{ letterSpacing: "0.06em" }}
                  >
                    {l.name}
                  </text>
                </g>
              );
            })}

            {/* Quake epicentres with ripples */}
            {ordered.map((q, i) => {
              const [x, y] = project(q.longitude, q.latitude);
              const r = magRadius(q.magnitude);
              const c = depthColor(q.depth);
              const isShallow = (q.depth ?? Infinity) <= 15;
              const isBig = (q.magnitude ?? 0) >= 5.5;
              const ageMs = q.time ? Date.now() - new Date(q.time).getTime() : 1e9;
              const fresh = ageMs < 6 * 60 * 60 * 1000; // 6h
              const rippleDur = 3.2 + (i % 5) * 0.4;
              const pingDelay = dotDelay(x, y);
              return (
                <g
                  key={q.publicID || `${x}-${y}-${i}`}
                  data-testid={IDS.dot}
                  onMouseEnter={() => setHovered(q)}
                  onMouseLeave={() => setHovered(null)}
                  style={{ cursor: "pointer" }}
                >
                  {fresh && (
                    <>
                      <circle cx={x} cy={y} r={r} fill="none" stroke={c} strokeWidth="0.6" opacity="0.85">
                        <animate
                          attributeName="r"
                          from={r}
                          to={r * (isBig ? 5 : 3.5)}
                          dur={`${rippleDur}s`}
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          from="0.65"
                          to="0"
                          dur={`${rippleDur}s`}
                          repeatCount="indefinite"
                        />
                      </circle>
                      <circle cx={x} cy={y} r={r} fill="none" stroke={c} strokeWidth="0.5" opacity="0.7">
                        <animate
                          attributeName="r"
                          from={r}
                          to={r * (isBig ? 6.5 : 4.5)}
                          dur={`${rippleDur * 1.3}s`}
                          repeatCount="indefinite"
                          begin={`${rippleDur / 2}s`}
                        />
                        <animate
                          attributeName="opacity"
                          from="0.5"
                          to="0"
                          dur={`${rippleDur * 1.3}s`}
                          repeatCount="indefinite"
                          begin={`${rippleDur / 2}s`}
                        />
                      </circle>
                    </>
                  )}
                  {/* Core dot */}
                  <circle
                    cx={x}
                    cy={y}
                    r={r * 0.55}
                    fill={c}
                    stroke="hsl(24 22% 6%)"
                    strokeWidth="0.4"
                    opacity="0.92"
                  />
                  {/* Radar ping -- expands once per sweep cycle, timed to the moment
                      the rotating sweep passes this dot's angle. */}
                  <circle cx={x} cy={y} r={r * 0.9} fill="none" stroke={c}>
                    <animate
                      attributeName="r"
                      values={`${r * 0.9};${r * (isBig ? 6.5 : 5)}`}
                      keyTimes="0;1"
                      dur={`${RADAR.period}s`}
                      begin={`${pingDelay}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.9;0.9;0"
                      keyTimes="0;0.04;0.28"
                      dur={`${RADAR.period}s`}
                      begin={`${pingDelay}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="stroke-width"
                      values="1.8;0.25"
                      keyTimes="0;1"
                      dur={`${RADAR.period}s`}
                      begin={`${pingDelay}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                  {/* Bright flash on the dot itself as the sweep touches */}
                  <circle cx={x} cy={y} r={r * 0.55} fill="hsl(41 60% 88%)" opacity="0">
                    <animate
                      attributeName="opacity"
                      values="0;0.95;0"
                      keyTimes="0;0.015;0.09"
                      dur={`${RADAR.period}s`}
                      begin={`${pingDelay}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                </g>
              );
            })}

            {/* Interactive slice: draggable centre + rotation handle. Line +
               band rotate around the centre so visitors can carve any
               transect (perfectly N-S when theta=0, E-W at 90°, diagonal
               otherwise). Only rendered when slice mode is on. */}
            {slice?.active && (() => {
              const [cx, cy] = project(slice.lng, slice.lat);
              const theta = slice.theta;
              const sinT = Math.sin(theta);
              const cosT = Math.cos(theta);
              // Line direction in SVG px space. In lat/lng: direction is
              // (sin(theta), cos(theta)) but latitude increases as y DECREASES
              // in SVG, so the SVG direction is (sin(theta), -cos(theta)).
              const halfLen = Math.hypot(MAP_VIEW.w, MAP_VIEW.h) * 0.7;
              const dx = sinT * halfLen;
              const dy = -cosT * halfLen;
              const endTopX = cx + dx;
              const endTopY = cy + dy;
              const endBotX = cx - dx;
              const endBotY = cy - dy;
              // Perpendicular offset for band edges (halfWidth in degrees ->
              // pixels using lng scale factor as a rough approximation).
              const degToPx =
                (MAP_VIEW.w / (179.5 - 165.5) + MAP_VIEW.h / (47.5 - 33.5)) / 2;
              const wPx = slice.halfWidth * degToPx;
              // Perp direction perpendicular to (dx, dy), unit-scaled to wPx
              const perpMag = Math.hypot(dx, dy) || 1;
              const perpX = (dy / perpMag) * wPx;
              const perpY = (-dx / perpMag) * wPx;
              const band = [
                [endTopX + perpX, endTopY + perpY],
                [endTopX - perpX, endTopY - perpY],
                [endBotX - perpX, endBotY - perpY],
                [endBotX + perpX, endBotY + perpY],
              ]
                .map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`)
                .join(" ");
              const bearingDeg = (((theta * 180) / Math.PI) + 360) % 360;
              return (
                <g data-testid="slice-line">
                  {/* Selection band (rotated polygon) */}
                  <polygon points={band} fill="hsl(14 66% 32%)" opacity="0.09" />
                  {/* Band edges */}
                  <line
                    x1={endTopX + perpX}
                    y1={endTopY + perpY}
                    x2={endBotX + perpX}
                    y2={endBotY + perpY}
                    stroke="hsl(14 66% 32%)"
                    strokeWidth="0.4"
                    strokeDasharray="3 3"
                    opacity="0.55"
                  />
                  <line
                    x1={endTopX - perpX}
                    y1={endTopY - perpY}
                    x2={endBotX - perpX}
                    y2={endBotY - perpY}
                    stroke="hsl(14 66% 32%)"
                    strokeWidth="0.4"
                    strokeDasharray="3 3"
                    opacity="0.55"
                  />
                  {/* Centre line */}
                  <line
                    x1={endTopX}
                    y1={endTopY}
                    x2={endBotX}
                    y2={endBotY}
                    stroke="hsl(14 66% 32%)"
                    strokeWidth="1.2"
                  />
                  {/* Rotation handle at the "north" end -- drag to spin */}
                  <g
                    transform={`translate(${(cx + dx * 0.28).toFixed(1)} ${(cy + dy * 0.28).toFixed(1)})`}
                    style={{ cursor: "grab", touchAction: "none" }}
                    data-testid="slice-rotate-handle"
                    onPointerDown={startDrag("rotate")}
                  >
                    <circle r="11" fill="hsl(41 44% 92%)" stroke="hsl(14 66% 32%)" strokeWidth="1.4" />
                    {/* Curved rotation glyph */}
                    <path
                      d="M -5 -1 A 5 5 0 1 1 5 -1 L 3 -3 M 5 -1 L 6 -4"
                      fill="none"
                      stroke="hsl(14 66% 32%)"
                      strokeWidth="1"
                      strokeLinecap="round"
                    />
                    <text
                      y="20"
                      textAnchor="middle"
                      className="mono"
                      fontSize="8"
                      fill="hsl(14 66% 32%)"
                      letterSpacing="1"
                    >
                      {bearingDeg.toFixed(0)}°
                    </text>
                  </g>
                  {/* Centre / translate handle */}
                  <g
                    transform={`translate(${cx.toFixed(1)} ${cy.toFixed(1)})`}
                    style={{ cursor: "move", touchAction: "none" }}
                    data-testid="slice-move-handle"
                    onPointerDown={startDrag("translate")}
                  >
                    <circle r="10" fill="hsl(41 44% 92%)" stroke="hsl(14 66% 32%)" strokeWidth="1.4" />
                    <path d="M -6 0 L -3 -3 M -6 0 L -3 3 M 6 0 L 3 -3 M 6 0 L 3 3 M 0 -6 L -3 -3 M 0 -6 L 3 -3 M 0 6 L -3 3 M 0 6 L 3 3" stroke="hsl(14 66% 32%)" strokeWidth="1" fill="none" />
                  </g>
                  {/* Corridor width handle -- perpendicular to the transect;
                      drag inward/outward to tighten or widen from ±0.2° to ±2°. */}
                  <g
                    transform={`translate(${(cx + perpX).toFixed(1)} ${(cy + perpY).toFixed(1)})`}
                    style={{ cursor: "grab", touchAction: "none" }}
                    data-testid="slice-width-handle"
                    onPointerDown={startDrag("width")}
                  >
                    <rect
                      x="-8"
                      y="-8"
                      width="16"
                      height="16"
                      fill="hsl(41 44% 92%)"
                      stroke="hsl(14 66% 32%)"
                      strokeWidth="1.4"
                      transform="rotate(45)"
                    />
                    {/* Double-arrow "expand" glyph */}
                    <path
                      d="M -5 0 L 5 0 M -5 0 L -2 -3 M -5 0 L -2 3 M 5 0 L 2 -3 M 5 0 L 2 3"
                      stroke="hsl(14 66% 32%)"
                      strokeWidth="1"
                      fill="none"
                      strokeLinecap="round"
                    />
                    <text
                      x="0"
                      y="22"
                      textAnchor="middle"
                      className="mono"
                      fontSize="8"
                      fill="hsl(14 66% 32%)"
                      letterSpacing="1"
                    >
                      ±{slice.halfWidth.toFixed(2)}°
                    </text>
                  </g>
                </g>
              );
            })()}

            {/* Radar sweep -- ink line + faint trailing wedge, rotating around
                the pivot. Drawn on top of everything so the sweep visibly
                crosses over both islands and the tremor dots. */}
            <g data-testid={IDS.radar} pointerEvents="none">
              <g transform={`translate(${RADAR.cx} ${RADAR.cy})`}>
                <g>
                  {/* Trailing wedge: from the leading line back ~55deg */}
                  <path
                    d={(() => {
                      const R = RADAR.reach;
                      const a = (55 * Math.PI) / 180;
                      const tx = -Math.sin(a) * R;
                      const ty = -Math.cos(a) * R;
                      return `M 0 0 L 0 ${-R} A ${R} ${R} 0 0 0 ${tx.toFixed(1)} ${ty.toFixed(1)} Z`;
                    })()}
                    fill="url(#radarTail)"
                    opacity="0.85"
                  />
                  {/* Leading edge line */}
                  <line
                    x1="0"
                    y1="0"
                    x2="0"
                    y2={-RADAR.reach}
                    stroke="hsl(14 66% 32%)"
                    strokeWidth="0.9"
                    opacity="0.55"
                  />
                  <line
                    x1="0"
                    y1="0"
                    x2="0"
                    y2={-RADAR.reach}
                    stroke="hsl(24 22% 10%)"
                    strokeWidth="0.35"
                    opacity="0.7"
                    strokeDasharray="2 3"
                  />
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0"
                    to="360"
                    dur={`${RADAR.period}s`}
                    repeatCount="indefinite"
                  />
                </g>
              </g>
              {/* Radar pivot marker */}
              <circle cx={RADAR.cx} cy={RADAR.cy} r="3" fill="none" stroke="hsl(24 22% 10%)" strokeWidth="0.8" />
              <circle cx={RADAR.cx} cy={RADAR.cy} r="1" fill="hsl(24 22% 10%)" />
              <line x1={RADAR.cx - 6} y1={RADAR.cy} x2={RADAR.cx - 3} y2={RADAR.cy} stroke="hsl(24 22% 10%)" strokeWidth="0.6" />
              <line x1={RADAR.cx + 3} y1={RADAR.cy} x2={RADAR.cx + 6} y2={RADAR.cy} stroke="hsl(24 22% 10%)" strokeWidth="0.6" />
              <line x1={RADAR.cx} y1={RADAR.cy - 6} x2={RADAR.cx} y2={RADAR.cy - 3} stroke="hsl(24 22% 10%)" strokeWidth="0.6" />
              <line x1={RADAR.cx} y1={RADAR.cy + 3} x2={RADAR.cx} y2={RADAR.cy + 6} stroke="hsl(24 22% 10%)" strokeWidth="0.6" />
            </g>

            {/* Hover tooltip */}
            {hovered && (
              (() => {
                const [hx, hy] = project(hovered.longitude, hovered.latitude);
                const tx = Math.min(MAP_VIEW.w - 150, hx + 10);
                const ty = Math.max(20, hy - 26);
                return (
                  <g pointerEvents="none">
                    <rect
                      x={tx}
                      y={ty - 14}
                      width="150"
                      height="42"
                      fill="hsl(41 44% 92%)"
                      stroke="hsl(24 22% 10%)"
                      strokeWidth="0.8"
                    />
                    <text x={tx + 6} y={ty} fontSize="9" fill="hsl(24 22% 10%)" className="mono">
                      M {hovered.magnitude?.toFixed(1)} · {fmtAgo(hovered.time)} ago
                    </text>
                    <text
                      x={tx + 6}
                      y={ty + 14}
                      fontSize="9"
                      fill="hsl(24 22% 22%)"
                      style={{ letterSpacing: "0.02em" }}
                    >
                      {(hovered.locality || "").slice(0, 26)}
                    </text>
                    <text
                      x={tx + 6}
                      y={ty + 25}
                      fontSize="7.5"
                      fill="hsl(24 22% 40%)"
                      className="mono"
                    >
                      depth {Math.round(hovered.depth ?? 0)}km · MMI {hovered.mmi ?? "—"}
                    </text>
                  </g>
                );
              })()
            )}
          </svg>
        </div>

        {/* Legend / side notes */}
        <div className="border-t md:border-t-0 md:border-l border-[hsl(var(--ink))]/25 p-5 space-y-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-2))] mb-2">
              Ripple size = magnitude
            </div>
            <div className="flex items-end gap-4">
              {[3, 4.5, 6].map((m) => (
                <div key={m} className="flex flex-col items-center gap-1">
                  <svg width={magRadius(m) * 2 + 4} height={magRadius(m) * 2 + 4}>
                    <circle
                      cx={(magRadius(m) * 2 + 4) / 2}
                      cy={(magRadius(m) * 2 + 4) / 2}
                      r={magRadius(m) * 0.55}
                      fill="hsl(24 22% 25%)"
                    />
                  </svg>
                  <span className="mono text-[9px] text-[hsl(var(--ink-mid))]">M {m.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </div>

          <div data-testid="depth-legend">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-2))] mb-2">
              Colour = depth
            </div>
            <ul className="space-y-1">
              {DEPTH_BINS.map((b) => (
                <li key={b.label} className="flex items-center gap-2">
                  <span
                    className="inline-block w-3.5 h-3.5 rounded-full border"
                    style={{
                      backgroundColor: b.color,
                      borderColor: "hsl(24 22% 8%)",
                    }}
                  />
                  <span className="mono text-[10px] text-[hsl(var(--ink))]">
                    {b.label}
                  </span>
                  {b.feels && (
                    <span className="mono text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--seismic))] ml-auto">
                      he feels
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-2))] mb-2">
              He feels
            </div>
            <ul className="text-sm space-y-1">
              <li className="flex justify-between">
                <span className="text-[hsl(var(--ink-mid))]">Nearest tremor</span>
                <span className="mono">
                  {visible[0]?.magnitude != null ? `M ${visible[0].magnitude.toFixed(1)}` : "—"}
                </span>
              </li>
              <li className="flex justify-between">
                <span className="text-[hsl(var(--ink-mid))]">Largest recent</span>
                <span className="mono">
                  {(() => {
                    const m = Math.max(0, ...visible.map((q) => q.magnitude ?? 0));
                    return m > 0 ? `M ${m.toFixed(1)}` : "—";
                  })()}
                </span>
              </li>
              <li className="flex justify-between">
                <span className="text-[hsl(var(--ink-mid))]">Shallow (≤15 km)</span>
                <span className="mono text-[hsl(var(--seismic))]">
                  {visible.filter((q) => (q.depth ?? Infinity) <= 15).length}
                </span>
              </li>
              <li className="flex justify-between">
                <span className="text-[hsl(var(--ink-mid))]">Plotted</span>
                <span className="mono">{visible.length}</span>
              </li>
            </ul>
          </div>

          <p className="font-display italic text-sm text-[hsl(var(--ink))]/85 leading-snug">
            Warm dots are shallow shocks close to his belly. Cool blues sit far
            below—the earth murmuring in its sleep.
          </p>
        </div>
      </div>
    </section>
  );
}
